/**
 * Seating charts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_classroom`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const seatingChartsPack: ToilePack = {
    slug: 'seating-charts',
    category: 'classroom',
    name: 'Seating charts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'rows', label: 'Rows', subject: "five straight rows of desks facing the front, the teacher's desk and whiteboard marked along the top edge"},
        {key: 'pods', label: 'Pods', subject: 'desks grouped into pods of four, a reading rug in one corner and the door marked on the side wall'},
        {key: 'horseshoe', label: 'Horseshoe', subject: "desks in a wide horseshoe open toward the front, the teacher's desk inside the opening, a bookshelf along the back wall"},
    ],
    styles: [
        {key: 'plan', label: 'Line plan', style: 'clean flat floor-plan drawing, thin uniform outlines and rounded corners, no perspective and no shadows', palette: 'black line on white with one soft grey fill', framing: 'The plan centered on a landscape page with wide margins.'},
        {key: 'coded', label: 'Colour-coded', style: 'friendly classroom map, soft rounded shapes and flat fills, each group of desks in its own colour', palette: 'muted teal, coral and butter yellow on cream', framing: 'The plan filling the page, margins even on all four sides.'},
        {key: 'sketch', label: 'Hand-drawn', style: 'hand-drawn planner style, slightly wobbly fine-pen lines with a doodled rug and a potted plant', palette: 'black ink with one warm highlighter yellow on white paper', framing: 'The plan drawn on a plain page with a generous margin all round.'},
    ],
    directions: ['for a computer lab', 'for thirty desks', 'with a reading corner'],
    seo: {
        title: 'Seating chart template: rows, pods, horseshoe',
        metaDescription: 'Nine seating chart templates — rows, pods and horseshoe — as a line plan, colour-coded and hand-drawn, every desk left blank.',
        h1: 'Seating chart: rows, pods and horseshoe layouts in three styles',
        tagline: 'Nine seating charts, three layouts, three looks — every desk a blank label.',
        intro: "The teacher's bird's-eye map of the room: desks in rows, pods of four, or a horseshoe, with the door, the teacher's desk and the rug marked. These nine print in three layouts, each in three looks — a plain line plan, a colour-coded version, a hand-drawn one — with every desk left blank to fill in with pencil. Type a different room shape and the chart redraws the same way.",
        useCases: [
            'a classroom seating plan for the quarter',
            "a substitute teacher's desk map",
            'a parent-teacher conference reference',
            'a printable seating plan template',
        ],
        faq: [
            {q: 'Are the desks labeled with names?', a: 'No — every desk prints as a blank rounded rectangle so it can be filled in with pencil and redrawn each time the seating changes.'},
            {q: 'Can I get a different room layout?', a: 'Yes — type a different room shape or size and the chart redraws in the same three styles.'},
            {q: 'What size does it print at?', a: 'Landscape A4 proportions (1216 by 832), wide enough to read the whole room map at a glance.'},
        ],
    },
}
